import React from 'react';
import PropTypes from 'prop-types';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  ClickAwayListener,
  Grow,
  IconButton,
  InputAdornment,
  MenuItem,
  MenuList,
  Paper,
  Popper,
  TablePagination,
  TextField,
  Tooltip,
  Typography
} from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ClearIcon from '@material-ui/icons/Clear';
import SearchInput from 'components/SearchInput';
import { useDebounce } from 'helpers';
import Skeleton from 'react-loading-skeleton';

const useStyles = makeStyles(theme => ({
  popper: {
    zIndex: theme.zIndex.appBar - 1
  },
  panel: {
    width: '700px',
    maxWidth: '90vw',
    maxHeight: '70vh',
    padding: '8px'
  },
  contentLayout: {
    display: 'flex',
    height: '420px',
    overflow: 'hidden'
  },
  leftPanel: {
    width: '260px',
    minWidth: '240px',
    maxWidth: '260px',
    flexShrink: 0,
    borderRight: `1px solid ${theme.palette.divider}`,
    paddingRight: '10px',
    marginRight: '10px',
    overflowY: 'auto',
    overflowX: 'hidden'
  },
  rightPanel: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    boxSizing: 'border-box',
    paddingBottom: '8px'
  },
  filtersTitle: {
    fontSize: '0.8125rem',
    fontWeight: 600,
    color: theme.palette.text.primary,
    marginBottom: '6px'
  },
  filterAccordion: {
    boxShadow: 'none',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: '6px',
    marginBottom: '8px',
    '&:before': {
      display: 'none'
    },
    '&.Mui-expanded': {
      margin: '0 0 8px 0'
    }
  },
  filterAccordionSummary: {
    minHeight: '38px',
    '&.Mui-expanded': {
      minHeight: '38px'
    }
  },
  filterAccordionSummaryContent: {
    margin: '8px 0',
    '&.Mui-expanded': {
      margin: '8px 0'
    }
  },
  filterHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%'
  },
  filterHeaderTitle: {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
    fontWeight: 600
  },
  clearAction: {
    color: theme.palette.primary.main,
    fontSize: '0.7rem',
    fontWeight: 600,
    cursor: 'pointer',
    '&:hover': {
      textDecoration: 'underline'
    }
  },
  filterAccordionDetails: {
    width: '100%',
    paddingTop: 0,
    paddingBottom: '8px',
    overflow: 'hidden',
    boxSizing: 'border-box'
  },
  searchContainer: {
    marginBottom: '8px',
    flexShrink: 0
  },
  list: {
    flex: 1,
    minHeight: 0,
    overflow: 'auto',
    border: 'none',
    borderRadius: 0
  },
  menuItem: {
    alignItems: 'flex-start',
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1)
  },
  optionContainer: {
    width: '100%'
  },
  emptyStateItem: {
    justifyContent: 'center',
    textAlign: 'center'
  },
  emptyStateText: {
    width: '100%',
    textAlign: 'center'
  },
  optionPrimary: {
    display: 'block',
    width: '100%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  optionSecondary: {
    color: 'rgba(0, 0, 0, 0.6)',
    fontSize: '0.75rem',
    lineHeight: 1.3,
    display: 'block',
    width: '100%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  paginationContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: '8px',
    flexShrink: 0,
    marginBottom: '4px',
    paddingBottom: '8px',
    minHeight: '52px',
    alignItems: 'center',
    '& .MuiTablePagination-root': {
      overflow: 'visible'
    },
    '& .MuiTablePagination-toolbar': {
      minHeight: '40px',
      alignItems: 'center',
      paddingBottom: '4px'
    },
    '& .MuiTablePagination-displayedRows, & .MuiTablePagination-selectLabel': {
      marginBottom: 0
    }
  },
  hierarchyOptions: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '6px'
  },
  hierarchyChip: {
    height: '26px',
    borderRadius: '14px',
    '& .MuiChip-label': {
      fontSize: '0.75rem',
      fontWeight: 500,
      paddingLeft: '10px',
      paddingRight: '10px'
    }
  },
  tagOptions: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '6px',
    width: '100%',
    maxHeight: '170px',
    overflowY: 'auto',
    overflowX: 'hidden',
    alignContent: 'flex-start',
    paddingRight: '2px'
  },
  tagChip: {
    maxWidth: '170px',
    height: '26px',
    borderRadius: '14px',
    '& .MuiChip-label': {
      display: 'block',
      fontSize: '0.75rem',
      fontWeight: 500,
      paddingLeft: '10px',
      paddingRight: '10px',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      maxWidth: '150px'
    }
  },
  emptyFilterText: {
    fontSize: '0.75rem',
    color: 'rgba(0, 0, 0, 0.6)',
    padding: '2px 0'
  },
  textField: {
    '& .MuiInputBase-input': {
      cursor: 'pointer'
    }
  },
  inputSkeleton: {
    width: '100%',
    lineHeight: 0,
    marginTop: '4px',
    marginBottom: '2px'
  }
}));

const emptyOptions = {
  list: [],
  valueId: 'uuid'
};

const TemplateSingleSelect = props => {
  const {
    id,
    className,
    name,
    label,
    value,
    options = emptyOptions,
    onChange,
    onFocus,
    onBlur,
    disabled,
    error,
    helperText,
    required,
    fullWidth,
    margin,
    dataTestId,
    selectedDisplayValue,
    remotePagination,
    searchValue: controlledSearchValue,
    pageNo: controlledPageNo,
    rowsPerPage: controlledRowsPerPage,
    totalCount,
    isLoading,
    hierarchyOptions,
    selectedHierarchyValues,
    statusOptions,
    selectedStatusValue,
    searchByOptions,
    selectedSearchByValues,
    campaignTypeOptions,
    selectedCampaignTypeValue,
    smsProviderOptions,
    selectedSmsProviderValue,
    promotionalFilterOptions,
    selectedPromotionalValue,
    tagOptions,
    selectedTagValues,
    onOpen,
    onHierarchyChange,
    onStatusChange,
    onSearchByChange,
    onCampaignTypeChange,
    onSmsProviderChange,
    onPromotionalFilterChange,
    onTagChange,
    onSearchChange,
    onPageChange,
    onRowsPerPageChange,
    showFieldSkeleton,
    popperPlacement,
    panelClassName,
    textFieldProps,
    emptyStateText = 'No options available',
    onClear
  } = props;

  const classes = useStyles();
  const { list = [], valueId = 'uuid' } = options;
  const anchorRef = React.useRef(null);
  const selectedItemRef = React.useRef(null);
  const hasAutoScrolledSelectionRef = React.useRef(false);

  const [open, setOpen] = React.useState(false);
  const [searchValue, setSearchValue] = React.useState('');
  const [pageNo, setPageNo] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const [remoteSearchValue, setRemoteSearchValue] = React.useState('');
  const [activeTagTooltip, setActiveTagTooltip] = React.useState(null);
  const [isRemoteSearchUserTriggered, setIsRemoteSearchUserTriggered] =
    React.useState(false);

  const isRemote = !!remotePagination;
  const debouncedRemoteSearch = useDebounce(remoteSearchValue, 500);

  React.useEffect(() => {
    if (!isRemote || open) {
      return;
    }
    setRemoteSearchValue(controlledSearchValue || '');
  }, [isRemote, controlledSearchValue, open]);

  React.useEffect(() => {
    if (!isRemote || !open) {
      return;
    }
    if (!isRemoteSearchUserTriggered) {
      return;
    }
    if ((debouncedRemoteSearch || '') !== (remoteSearchValue || '')) {
      return;
    }
    const normalizedSearch = (debouncedRemoteSearch || '').trim();
    if (typeof onSearchChange === 'function') {
      onSearchChange(normalizedSearch);
    }
  }, [
    isRemote,
    debouncedRemoteSearch,
    remoteSearchValue,
    open,
    isRemoteSearchUserTriggered
  ]);

  const effectiveSearchValue =
    isRemote
      ? remoteSearchValue
      : searchValue;
  const effectivePageNo =
    isRemote && Number.isInteger(controlledPageNo) ? controlledPageNo : pageNo;
  const effectiveRowsPerPage =
    isRemote && Number.isInteger(controlledRowsPerPage)
      ? controlledRowsPerPage
      : rowsPerPage;

  const selectedOption = list.find(option => option?.[valueId] === value) || null;
  const selectedLabel = selectedOption?.name || selectedDisplayValue || '';

  const filteredOptions = React.useMemo(() => {
    if (isRemote) {
      return list;
    }

    const query = (searchValue || '').trim().toLowerCase();
    if (!query) {
      return list;
    }

    return list.filter(option => {
      const defaultSearch = option?.name?.toLowerCase() || '';
      const customSearch = option?.searchText || '';
      return (`${defaultSearch} ${customSearch}`).includes(query);
    });
  }, [isRemote, list, searchValue]);

  const paginatedOptions = React.useMemo(() => {
    if (isRemote) {
      return filteredOptions;
    }

    const start = pageNo * rowsPerPage;
    return filteredOptions.slice(start, start + rowsPerPage);
  }, [isRemote, filteredOptions, pageNo, rowsPerPage]);

  React.useEffect(() => {
    if (!isRemote) {
      setPageNo(0);
    }
  }, [searchValue]);

  React.useEffect(() => {
    if (!isRemote) {
      setPageNo(0);
    }
  }, [selectedPromotionalValue, selectedCampaignTypeValue, isRemote]);

  const handleChange = selected => {
    if (selected?.disabled) {
      return;
    }

    if (typeof onChange === 'function') {
      onChange({
        target: {
          name,
          value: selected ? selected[valueId] : ''
        }
      });
    }

    setOpen(false);
  };

  const handleToggle = () => {
    if (!disabled) {
      setOpen(prev => {
        const nextOpen = !prev;
        if (nextOpen) {
          const initialSearchValue = selectedLabel || '';
          if (typeof onOpen === 'function') {
            onOpen(initialSearchValue);
          }
          if (isRemote) {
            setIsRemoteSearchUserTriggered(false);
            setRemoteSearchValue(initialSearchValue);
          } else {
            setSearchValue(initialSearchValue);
            const selectedIndex = list.findIndex(
              option => option?.[valueId] === value
            );
            if (selectedIndex >= 0) {
              setPageNo(Math.floor(selectedIndex / rowsPerPage));
            } else {
              setPageNo(0);
            }
          }
        }
        return nextOpen;
      });
    }
  };

  React.useEffect(() => {
    if (!open) {
      hasAutoScrolledSelectionRef.current = false;
      return;
    }

    if (hasAutoScrolledSelectionRef.current || !selectedItemRef.current) {
      return;
    }

    selectedItemRef.current.scrollIntoView({ block: 'nearest' });
    hasAutoScrolledSelectionRef.current = true;
  }, [open, value]);

  React.useEffect(() => {
    if (!open && activeTagTooltip !== null) {
      setActiveTagTooltip(null);
    }
  }, [open, activeTagTooltip]);

  const handleClose = event => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }

    setActiveTagTooltip(null);
    setOpen(false);
  };

  const handleSearchChange = event => {
    const nextValue = event.target.value || '';
    if (isRemote) {
      setIsRemoteSearchUserTriggered(true);
      setRemoteSearchValue(nextValue);
      return;
    }
    setSearchValue(nextValue);
  };

  const resetSearch = () => {
    if (isRemote) {
      setIsRemoteSearchUserTriggered(true);
      setRemoteSearchValue('');
      return;
    }

    setSearchValue('');
    setPageNo(0);
  };

  const handlePageChange = (_, page) => {
    if (isRemote) {
      if (typeof onPageChange === 'function') {
        onPageChange(page);
      }
      return;
    }

    setPageNo(page);
  };

  const handleRowsPerPageChange = event => {
    const nextRowsPerPage = parseInt(event.target.value, 10);
    if (isRemote) {
      if (typeof onRowsPerPageChange === 'function') {
        onRowsPerPageChange(nextRowsPerPage);
      }
      return;
    }

    setRowsPerPage(nextRowsPerPage);
    setPageNo(0);
  };

  const handleHierarchyToggle = hierarchyValue => {
    const currentValues = Array.isArray(selectedHierarchyValues)
      ? selectedHierarchyValues
      : [];
    const isSelected = currentValues.includes(hierarchyValue);
    const updatedValues = isSelected
      ? currentValues.filter(value => value !== hierarchyValue)
      : [...currentValues, hierarchyValue];

    if (typeof onHierarchyChange === 'function') {
      onHierarchyChange(updatedValues);
    }
  };

  const handleTagToggle = tagValue => {
    const currentValues = Array.isArray(selectedTagValues)
      ? selectedTagValues
      : [];
    const isSelected = currentValues.includes(tagValue);
    const updatedValues = isSelected
      ? currentValues.filter(value => value !== tagValue)
      : [...currentValues, tagValue];

    if (typeof onTagChange === 'function') {
      onTagChange(updatedValues);
    }
  };

  const handleStatusToggle = statusValue => {
    if (typeof onStatusChange === 'function') {
      onStatusChange(statusValue);
    }
  };

  const handleSearchByToggle = searchByValue => {
    const currentValues = Array.isArray(selectedSearchByValues)
      ? selectedSearchByValues
      : [];
    const isSelected = currentValues.includes(searchByValue);
    const updatedValues = isSelected
      ? []
      : [searchByValue];

    if (typeof onSearchByChange === 'function') {
      onSearchByChange(updatedValues);
    }
  };

  const handleCampaignTypeToggle = campaignTypeValue => {
    if (typeof onCampaignTypeChange === 'function') {
      onCampaignTypeChange(campaignTypeValue);
    }
  };

  const handleSmsProviderToggle = smsProviderValue => {
    if (typeof onSmsProviderChange === 'function') {
      onSmsProviderChange(smsProviderValue);
    }
  };

  const clearSearchByCategory = event => {
    event.preventDefault();
    event.stopPropagation();
    if (typeof onSearchByChange === 'function') {
      onSearchByChange([]);
    }
  };

  const clearCampaignTypeCategory = event => {
    event.preventDefault();
    event.stopPropagation();
    if (typeof onCampaignTypeChange === 'function') {
      onCampaignTypeChange('EMAIL');
    }
  };

  const clearSmsProviderCategory = event => {
    event.preventDefault();
    event.stopPropagation();
    if (typeof onSmsProviderChange === 'function') {
      onSmsProviderChange('VONAGE');
    }
  };

  const handlePromotionalFilterToggle = value => {
    if (typeof onPromotionalFilterChange === 'function') {
      onPromotionalFilterChange(value);
    }
  };

  const clearPromotionalFilterCategory = event => {
    event.preventDefault();
    event.stopPropagation();
    if (typeof onPromotionalFilterChange === 'function') {
      onPromotionalFilterChange('all');
    }
  };

  const clearHierarchyCategory = event => {
    event.preventDefault();
    event.stopPropagation();
    if (typeof onHierarchyChange === 'function') {
      onHierarchyChange([]);
    }
  };

  const clearTagCategory = event => {
    event.preventDefault();
    event.stopPropagation();
    if (typeof onTagChange === 'function') {
      onTagChange([]);
    }
  };

  const clearStatusCategory = event => {
    event.preventDefault();
    event.stopPropagation();
    if (typeof onStatusChange === 'function') {
      onStatusChange('all');
    }
  };

  const handleAdornmentToggle = event => {
    event.preventDefault();
    event.stopPropagation();
    handleToggle();
  };

  const closeTagTooltip = () => {
    setActiveTagTooltip(null);
  };

  return (
    <>
      {showFieldSkeleton ? (
        <div className={classes.inputSkeleton}>
          <Skeleton height={40} />
        </div>
      ) : (
      <TextField
        id={id || name}
        data-testid={dataTestId || `template-single-select-${name}`}
        variant="outlined"
        margin={margin || 'dense'}
        fullWidth={fullWidth}
        required={required}
        label={label}
        name={name}
        error={error}
        helperText={helperText}
        value={selectedLabel}
        disabled={disabled}
        onFocus={onFocus}
        onBlur={onBlur}
        inputRef={anchorRef}
        onClick={handleToggle}
        className={`${classes.textField} ${className || ''}`.trim()}
        InputProps={{
          readOnly: true,
          endAdornment: (
            <InputAdornment position="end">
              {typeof onClear === 'function' && (
                <IconButton
                  size="small"
                  style={{ visibility: !!value ? 'visible' : 'hidden' }}
                  tabIndex={-1}
                  onMouseDown={event => {
                    event.preventDefault();
                    event.stopPropagation();
                  }}
                  onClick={event => {
                    event.preventDefault();
                    event.stopPropagation();
                    onClear();
                  }}
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              )}
              <IconButton
                size="small"
                onMouseDown={event => {
                  event.preventDefault();
                  event.stopPropagation();
                }}
                onClick={handleAdornmentToggle}
              >
                <ExpandMoreIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          )
        }}
        {...textFieldProps}
      />
      )}

      <Popper
        open={open}
        anchorEl={anchorRef.current}
        placement={popperPlacement || 'right-start'}
        modifiers={{
          flip: {
            enabled: true,
            behavior: ['bottom-start', 'top-start', 'right-start', 'left-start']
          },
          preventOverflow: {
            enabled: true,
            boundariesElement: 'viewport',
            padding: 8
          }
        }}
        transition
        className={classes.popper}
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin:
                placement === 'bottom' ? 'center top' : 'center bottom'
            }}
          >
            <Paper>
              <ClickAwayListener onClickAway={handleClose}>
                <div className={`${classes.panel} ${panelClassName || ''}`.trim()}>
                  <div className={classes.contentLayout}>
                    <div className={classes.leftPanel} onScroll={closeTagTooltip}>
                      <Typography className={classes.filtersTitle}>Filters</Typography>


                      {Array.isArray(hierarchyOptions) && hierarchyOptions.length > 0 && (
                        <Accordion className={classes.filterAccordion}>
                          <AccordionSummary
                            expandIcon={<ExpandMoreIcon fontSize="small" />}
                            className={classes.filterAccordionSummary}
                            classes={{ content: classes.filterAccordionSummaryContent }}
                          >
                            <div className={classes.filterHeader}>
                              <Typography className={classes.filterHeaderTitle}>Hierarchy</Typography>
                              <Typography
                                className={classes.clearAction}
                                onClick={clearHierarchyCategory}
                              >
                                Clear
                              </Typography>
                            </div>
                          </AccordionSummary>
                          <AccordionDetails className={classes.filterAccordionDetails}>
                            <Box className={classes.hierarchyOptions}>
                              {hierarchyOptions.map(option => (
                                <Chip
                                  key={option.value}
                                  className={classes.hierarchyChip}
                                  clickable
                                  color={
                                    Array.isArray(selectedHierarchyValues) &&
                                    selectedHierarchyValues.includes(option.value)
                                      ? 'primary'
                                      : 'default'
                                  }
                                  variant={
                                    Array.isArray(selectedHierarchyValues) &&
                                    selectedHierarchyValues.includes(option.value)
                                      ? 'default'
                                      : 'outlined'
                                  }
                                  onClick={() => handleHierarchyToggle(option.value)}
                                  label={option.searchScope || option.label || option.value}
                                />
                              ))}
                            </Box>
                          </AccordionDetails>
                        </Accordion>
                      )}

                      {Array.isArray(statusOptions) && statusOptions.length > 0 && (
                        <Accordion defaultExpanded className={classes.filterAccordion}>
                          <AccordionSummary
                            expandIcon={<ExpandMoreIcon fontSize="small" />}
                            className={classes.filterAccordionSummary}
                            classes={{ content: classes.filterAccordionSummaryContent }}
                          >
                            <div className={classes.filterHeader}>
                              <Typography className={classes.filterHeaderTitle}>Status</Typography>
                              <Typography
                                className={classes.clearAction}
                                onClick={clearStatusCategory}
                              >
                                Clear
                              </Typography>
                            </div>
                          </AccordionSummary>
                          <AccordionDetails className={classes.filterAccordionDetails}>
                            <Box className={classes.hierarchyOptions}>
                              {statusOptions.map(option => (
                                <Chip
                                  key={option.value}
                                  className={classes.hierarchyChip}
                                  clickable
                                  color={
                                    (selectedStatusValue || 'all') === option.value
                                      ? 'primary'
                                      : 'default'
                                  }
                                  variant={
                                    (selectedStatusValue || 'all') === option.value
                                      ? 'default'
                                      : 'outlined'
                                  }
                                  onClick={() => handleStatusToggle(option.value)}
                                  label={option.label || option.value}
                                />
                              ))}
                            </Box>
                          </AccordionDetails>
                        </Accordion>
                      )}

                      {Array.isArray(searchByOptions) && searchByOptions.length > 0 && (
                        <Accordion defaultExpanded className={classes.filterAccordion}>
                          <AccordionSummary
                            expandIcon={<ExpandMoreIcon fontSize="small" />}
                            className={classes.filterAccordionSummary}
                            classes={{ content: classes.filterAccordionSummaryContent }}
                          >
                            <div className={classes.filterHeader}>
                              <Typography className={classes.filterHeaderTitle}>Search By</Typography>
                              <Typography
                                className={classes.clearAction}
                                onClick={clearSearchByCategory}
                              >
                                Clear
                              </Typography>
                            </div>
                          </AccordionSummary>
                          <AccordionDetails className={classes.filterAccordionDetails}>
                            <Box className={classes.hierarchyOptions}>
                              {searchByOptions.map(option => (
                                <Chip
                                  key={option.value}
                                  className={classes.hierarchyChip}
                                  clickable
                                  color={
                                    Array.isArray(selectedSearchByValues) &&
                                    selectedSearchByValues.includes(option.value)
                                      ? 'primary'
                                      : 'default'
                                  }
                                  variant={
                                    Array.isArray(selectedSearchByValues) &&
                                    selectedSearchByValues.includes(option.value)
                                      ? 'default'
                                      : 'outlined'
                                  }
                                  onClick={() => handleSearchByToggle(option.value)}
                                  label={option.label || option.value}
                                />
                              ))}
                            </Box>
                          </AccordionDetails>
                        </Accordion>
                      )}

                      {Array.isArray(campaignTypeOptions) && campaignTypeOptions.length > 0 && (
                        <Accordion defaultExpanded className={classes.filterAccordion}>
                          <AccordionSummary
                            expandIcon={<ExpandMoreIcon fontSize="small" />}
                            className={classes.filterAccordionSummary}
                            classes={{ content: classes.filterAccordionSummaryContent }}
                          >
                            <div className={classes.filterHeader}>
                              <Typography className={classes.filterHeaderTitle}>Campaign Type</Typography>
                              <Typography
                                className={classes.clearAction}
                                onClick={clearCampaignTypeCategory}
                              >
                                Clear
                              </Typography>
                            </div>
                          </AccordionSummary>
                          <AccordionDetails className={classes.filterAccordionDetails}>
                            <Box className={classes.hierarchyOptions}>
                              {campaignTypeOptions.map(option => (
                                <Chip
                                  key={option.value}
                                  className={classes.hierarchyChip}
                                  clickable
                                  color={
                                    (selectedCampaignTypeValue || 'EMAIL') === option.value
                                      ? 'primary'
                                      : 'default'
                                  }
                                  variant={
                                    (selectedCampaignTypeValue || 'EMAIL') === option.value
                                      ? 'default'
                                      : 'outlined'
                                  }
                                  onClick={() => handleCampaignTypeToggle(option.value)}
                                  label={option.label || option.value}
                                />
                              ))}
                            </Box>
                          </AccordionDetails>
                        </Accordion>
                      )}

                      {Array.isArray(smsProviderOptions) && smsProviderOptions.length > 0 && (selectedCampaignTypeValue || 'EMAIL') === 'SMS' && (
                        <Accordion defaultExpanded className={classes.filterAccordion}>
                          <AccordionSummary
                            expandIcon={<ExpandMoreIcon fontSize="small" />}
                            className={classes.filterAccordionSummary}
                            classes={{ content: classes.filterAccordionSummaryContent }}
                          >
                            <div className={classes.filterHeader}>
                              <Typography className={classes.filterHeaderTitle}>SMS Provider</Typography>
                              <Typography
                                className={classes.clearAction}
                                onClick={clearSmsProviderCategory}
                              >
                                Clear
                              </Typography>
                            </div>
                          </AccordionSummary>
                          <AccordionDetails className={classes.filterAccordionDetails}>
                            <Box className={classes.hierarchyOptions}>
                              {smsProviderOptions.map(option => (
                                <Chip
                                  key={option.value}
                                  className={classes.hierarchyChip}
                                  clickable
                                  color={
                                    (selectedSmsProviderValue || 'VONAGE') === option.value
                                      ? 'primary'
                                      : 'default'
                                  }
                                  variant={
                                    (selectedSmsProviderValue || 'VONAGE') === option.value
                                      ? 'default'
                                      : 'outlined'
                                  }
                                  onClick={() => handleSmsProviderToggle(option.value)}
                                  label={option.label || option.value}
                                />
                              ))}
                            </Box>
                          </AccordionDetails>
                        </Accordion>
                      )}

                      {Array.isArray(promotionalFilterOptions) && promotionalFilterOptions.length > 0 && (
                        <Accordion defaultExpanded className={classes.filterAccordion}>
                          <AccordionSummary
                            expandIcon={<ExpandMoreIcon fontSize="small" />}
                            className={classes.filterAccordionSummary}
                            classes={{ content: classes.filterAccordionSummaryContent }}
                          >
                            <div className={classes.filterHeader}>
                              <Typography className={classes.filterHeaderTitle}>Promotional</Typography>
                              <Typography
                                className={classes.clearAction}
                                onClick={clearPromotionalFilterCategory}
                              >
                                Clear
                              </Typography>
                            </div>
                          </AccordionSummary>
                          <AccordionDetails className={classes.filterAccordionDetails}>
                            <Box className={classes.hierarchyOptions}>
                              {promotionalFilterOptions.map(option => (
                                <Chip
                                  key={option.value}
                                  className={classes.hierarchyChip}
                                  clickable
                                  color={
                                    (selectedPromotionalValue || 'all') === option.value
                                      ? 'primary'
                                      : 'default'
                                  }
                                  variant={
                                    (selectedPromotionalValue || 'all') === option.value
                                      ? 'default'
                                      : 'outlined'
                                  }
                                  onClick={() => handlePromotionalFilterToggle(option.value)}
                                  label={option.label || option.value}
                                />
                              ))}
                            </Box>
                          </AccordionDetails>
                        </Accordion>
                      )}

                      {(Array.isArray(tagOptions) || typeof onTagChange === 'function') && (
                        <Accordion className={classes.filterAccordion}>
                          <AccordionSummary
                            expandIcon={<ExpandMoreIcon fontSize="small" />}
                            className={classes.filterAccordionSummary}
                            classes={{ content: classes.filterAccordionSummaryContent }}
                          >
                            <div className={classes.filterHeader}>
                              <Typography className={classes.filterHeaderTitle}>Tags</Typography>
                              <Typography
                                className={classes.clearAction}
                                onClick={clearTagCategory}
                              >
                                Clear
                              </Typography>
                            </div>
                          </AccordionSummary>
                          <AccordionDetails className={classes.filterAccordionDetails}>
                            {Array.isArray(tagOptions) && tagOptions.length > 0 ? (
                              <Box className={classes.tagOptions} onScroll={closeTagTooltip}>
                                {tagOptions.map(option => (
                                  <Tooltip
                                    key={option.value}
                                    title={option.label || option.value}
                                    arrow
                                    open={activeTagTooltip === option.value}
                                    onOpen={() => setActiveTagTooltip(option.value)}
                                    onClose={closeTagTooltip}
                                  >
                                    <Chip
                                      className={classes.tagChip}
                                      clickable
                                      color={
                                        Array.isArray(selectedTagValues) &&
                                        selectedTagValues.includes(option.value)
                                          ? 'primary'
                                          : 'default'
                                      }
                                      variant={
                                        Array.isArray(selectedTagValues) &&
                                        selectedTagValues.includes(option.value)
                                          ? 'default'
                                          : 'outlined'
                                      }
                                      onClick={() => handleTagToggle(option.value)}
                                      label={option.label || option.value}
                                    />
                                  </Tooltip>
                                ))}
                              </Box>
                            ) : (
                              <Typography className={classes.emptyFilterText}>
                                No tags available for this context
                              </Typography>
                            )}
                          </AccordionDetails>
                        </Accordion>
                      )}

                    </div>

                    <div className={classes.rightPanel}>
                      <div className={classes.searchContainer}>
                        <SearchInput
                          placeholder={label ? `Search ${label}` : 'Search'}
                          value={effectiveSearchValue}
                          onChange={handleSearchChange}
                          onClear={resetSearch}
                        />
                      </div>

                      <MenuList className={classes.list} onScroll={closeTagTooltip}>
                        {isLoading ? (
                          Array.from({ length: 5 }).map((_, index) => (
                            <MenuItem key={`loading-option-${index}`} disabled>
                              <div style={{ width: '100%' }}>
                                <Skeleton height={14} width="65%" />
                                <Skeleton height={10} width="45%" style={{ marginTop: 6 }} />
                              </div>
                            </MenuItem>
                          ))
                        ) : paginatedOptions.length === 0 ? (
                          <MenuItem disabled className={classes.emptyStateItem}>
                            <Typography className={classes.emptyStateText} variant="body2">
                              {emptyStateText}
                            </Typography>
                          </MenuItem>
                        ) : (
                          paginatedOptions.map(option => {
                            const optionNode = (
                              <MenuItem
                                key={option?.[valueId]}
                                ref={option?.[valueId] === value ? selectedItemRef : null}
                                className={classes.menuItem}
                                selected={option?.[valueId] === value}
                                disabled={!!option?.disabled}
                                onClick={() => !option?.disabled && handleChange(option)}
                              >
                                <div className={classes.optionContainer}>
                                  <Typography variant="body2">{option?.name}</Typography>
                                  {!!option?.secondaryText && (
                                    <Typography className={classes.optionSecondary}>
                                      {option.secondaryText}
                                    </Typography>
                                  )}
                                </div>
                              </MenuItem>
                            );

                            if (option?.toolTip) {
                              return (
                                <Tooltip key={`${option?.[valueId]}-tooltip`} title={option.toolTip}>
                                  <div>{optionNode}</div>
                                </Tooltip>
                              );
                            }

                            return optionNode;
                          })
                        )}
                      </MenuList>

                      <div className={classes.paginationContainer}>
                        <TablePagination
                          component="div"
                          count={isRemote ? totalCount || 0 : filteredOptions.length}
                          page={effectivePageNo}
                          rowsPerPage={effectiveRowsPerPage}
                          onPageChange={handlePageChange}
                          onRowsPerPageChange={handleRowsPerPageChange}
                          rowsPerPageOptions={[]}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </>
  );
};

TemplateSingleSelect.propTypes = {
  id: PropTypes.string,
  className: PropTypes.string,
  name: PropTypes.string.isRequired,
  label: PropTypes.string,
  value: PropTypes.string,
  options: PropTypes.object,
  onChange: PropTypes.func,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
  disabled: PropTypes.bool,
  error: PropTypes.bool,
  helperText: PropTypes.string,
  required: PropTypes.bool,
  fullWidth: PropTypes.bool,
  margin: PropTypes.string,
  dataTestId: PropTypes.string,
  selectedDisplayValue: PropTypes.string,
  remotePagination: PropTypes.bool,
  searchValue: PropTypes.string,
  pageNo: PropTypes.number,
  rowsPerPage: PropTypes.number,
  totalCount: PropTypes.number,
  isLoading: PropTypes.bool,
  hierarchyOptions: PropTypes.array,
  selectedHierarchyValues: PropTypes.array,
  statusOptions: PropTypes.array,
  selectedStatusValue: PropTypes.string,
  searchByOptions: PropTypes.array,
  selectedSearchByValues: PropTypes.array,
  campaignTypeOptions: PropTypes.array,
  selectedCampaignTypeValue: PropTypes.string,
  smsProviderOptions: PropTypes.array,
  selectedSmsProviderValue: PropTypes.string,
  promotionalFilterOptions: PropTypes.array,
  selectedPromotionalValue: PropTypes.string,
  tagOptions: PropTypes.array,
  selectedTagValues: PropTypes.array,
  onOpen: PropTypes.func,
  onHierarchyChange: PropTypes.func,
  onStatusChange: PropTypes.func,
  onSearchByChange: PropTypes.func,
  onCampaignTypeChange: PropTypes.func,
  onSmsProviderChange: PropTypes.func,
  onPromotionalFilterChange: PropTypes.func,
  onTagChange: PropTypes.func,
  onSearchChange: PropTypes.func,
  onPageChange: PropTypes.func,
  onRowsPerPageChange: PropTypes.func,
  showFieldSkeleton: PropTypes.bool,
  popperPlacement: PropTypes.string,
  panelClassName: PropTypes.string,
  textFieldProps: PropTypes.object,
  emptyStateText: PropTypes.string,
  onClear: PropTypes.func
};

export default TemplateSingleSelect;
